import type { RunState } from '../core/run';
import type { CoreEvent, CoreWorld } from '../core/tick';
import type { SaveStore } from './save-store';
import type { SaveFile, SaveSlot, Settings, SlotId } from './schema';

/**
 * Event-driven autosave (GDD §6.4, "cuándo se guarda"): every event writes only the fields of its row of
 * the table, as the whole document in one write, and stamps `updatedAt`. Nothing is written without an
 * active slot. The position in the middle of a stretch is never saved (GDD §6.3).
 */
export interface AutosaveOptions {
  store: SaveStore;
  /** The persistent state of the run in flight, read when a level ends or a checkpoint is crossed. */
  getRunState: () => RunState | undefined;
  /** ISO 8601 clock. */
  now: () => string;
}

export interface Autosave {
  /** Listener for the events the core dispatches after phase 6 (#30). */
  onCoreEvent(event: CoreEvent, world: CoreWorld): void;
  changeSetting<K extends keyof Settings>(key: K, value: Settings[K]): void;
  /** The page was hidden or is being unloaded: only play time and `updatedAt` are written. */
  pageHidden(): void;
  /** Whether time counts as play time: a level is loaded and the game is not paused. */
  setPlaying(playing: boolean): void;
  /** Pause events of the game: pausing stops the play time, resuming restarts it. */
  onPauseEvent(event: { type: string }): void;
  /** Flaps pressed since the last count; written with the next save event (@gdd-pending). */
  recordFlaps(count: number): void;
}

export function createAutosave({ store, getRunState, now }: AutosaveOptions): Autosave {
  let playing = false;
  let mark = Date.parse(now());
  let banked = 0;
  let pendingFlaps = 0;
  // Bosses defeated since the last level end: written with it (GDD §6.4, issue #89).
  let pendingBosses = 0;

  const setPlaying = (next: boolean) => {
    const at = Date.parse(now());
    if (playing) banked += at - mark;
    mark = at;
    playing = next;
  };

  const takePlayTime = () => {
    const at = Date.parse(now());
    const total = banked + (playing ? at - mark : 0);
    banked = 0;
    mark = at;
    return total;
  };

  /** Applies `change` to the active slot and writes the document once; no slot, no write. */
  const writeSlot = (change: (slot: SaveSlot) => Partial<SaveSlot>, document: (save: SaveFile) => SaveFile = (s) => s) => {
    const save = document(store.save);
    const slotId: SlotId | null = save.lastSlotId;
    const slot = slotId ? save.slots[slotId] : null;
    if (!slotId || !slot) return;
    const changed = { ...slot, ...change(slot) };
    const stamped: SaveSlot = {
      ...changed,
      updatedAt: now(),
      playTimeMs: slot.playTimeMs + takePlayTime(),
      stats: { ...changed.stats, flaps: changed.stats.flaps + pendingFlaps },
    };
    pendingFlaps = 0;
    store.write({ ...save, slots: { ...save.slots, [slotId]: stamped } });
  };

  const onCoreEvent = (event: CoreEvent, world: CoreWorld) => {
    // The test level OP-000 is outside the campaign: nothing of it is ever saved (GDD §16.4).
    if (getRunState()?.test) return;
    switch (event.type) {
      case 'boss:defeated':
        pendingBosses += 1;
        break;
      case 'CHECKPOINT_ACTIVATED':
        writeSlot((slot) => ({
          current: {
            ...slot.current,
            levelId: getRunState()?.levelId ?? slot.current.levelId,
            checkpointId: event.checkpointId as string,
            score: event.score as number,
            dollars: event.dollars as number,
            lives: event.lives as number,
            cleanRun: getRunState()?.cleanRun ?? slot.current.cleanRun,
          },
        }));
        break;
      case 'LIFE_LOST':
        // The last life is written by the game over, which restores the run; 0 lives is not a valid slot.
        if (event.livesLeft === 0) break;
        writeSlot((slot) => ({
          current: { ...slot.current, lives: event.livesLeft as number, cleanRun: false },
          stats: { ...slot.stats, deaths: world.deaths },
        }));
        break;
      case 'RUN_RESTORED':
        writeSlot((slot) => ({
          current: {
            ...slot.current,
            checkpointId: null,
            lives: event.lives as number,
            score: event.score as number,
            scoreAtLevelStart: event.scoreAtLevelStart as number,
            dollars: event.dollars as number,
            cleanRun: true,
          },
          stats: { ...slot.stats, deaths: world.deaths },
        }));
        break;
      case 'LEVEL_COMPLETED': {
        const state = getRunState();
        if (!state) break;
        writeSlot((slot) => ({
          unlockedLevels: [...state.unlockedLevels],
          completedLevels: structuredClone(state.completedLevels),
          current: {
            levelId: state.levelId,
            checkpointId: null,
            lives: world.lives,
            dollars: world.dollars,
            score: world.score,
            scoreAtLevelStart: state.scoreAtLevelStart,
            cleanRun: true,
          },
          // A PERFECTO landing of El Salto counts towards the stats (GDD §6.4, #93).
          stats: {
            ...slot.stats,
            bossesDefeated: slot.stats.bossesDefeated + pendingBosses,
            perfectJumps: slot.stats.perfectJumps + (event.jumpGrade === 'PERFECTO' ? 1 : 0),
          },
        }));
        pendingBosses = 0;
        break;
      }
    }
  };

  return {
    onCoreEvent,
    changeSetting: (key, value) =>
      writeSlot(
        () => ({}),
        (save) => ({ ...save, settings: { ...save.settings, [key]: value } }),
      ),
    pageHidden: () => writeSlot(() => ({})),
    setPlaying,
    onPauseEvent(event) {
      if (event.type === 'GAME_PAUSED') setPlaying(false);
      else if (event.type === 'GAME_RESUMED') setPlaying(true);
    },
    recordFlaps(count) {
      pendingFlaps += count;
    },
  };
}
